import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import type { UseQueryResult, UseInfiniteQueryResult, UseMutationResult, InfiniteData } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import type { IInfinitePaginatedResponse } from '~/api/page-containers'
import type { IPageContainer, IPageContainerCreateUpdate } from '~/types/page-container'
import type { ITranslationRuleError } from '~/types/translation'

import {
  checkPageContainerTranslations,
  compilePageContainer,
  processPageContainer,
  createPageContainerFromTemplate,
  updatePageContainerFromTemplateProcessor,
  createPageContainer,
  getPageContainer,
  getPageContainers,
} from '~/api/page-containers'

////////// KEYS //////////
export const pageContainersKeys = {
  all: ['page-containers'] as const,
  details: (id?: string) => [...pageContainersKeys.all, 'details', id] as const,
  infiniteList: (isOpen: boolean, params?: AxiosRequestConfig['params']) =>
    [...pageContainersKeys.all, 'infinite-list', isOpen, params] as const,
  list: (params?: AxiosRequestConfig['params']) => [...pageContainersKeys.all, 'list', params] as const,
}

////////// QUERIES //////////
export const usePageContainersListQuery = (
  params?: AxiosRequestConfig['params']
): UseQueryResult<IPaginatedResponse<IPageContainer>> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => getPageContainers(params),
    queryKey: pageContainersKeys.list(params),
  })
}

export const usePageContainersListInfiniteQuery = (
  isOpen: boolean,
  params?: AxiosRequestConfig['params']
): UseInfiniteQueryResult<InfiniteData<IInfinitePaginatedResponse<IPageContainer>>> => {
  return useInfiniteQuery({
    enabled: isOpen,
    getNextPageParam: () => params.page,
    initialPageParam: undefined,
    queryFn: ({ pageParam = params.page }) => getPageContainers({ ...params, page: pageParam }),
    queryKey: pageContainersKeys.infiniteList(isOpen, { ...params, page: '' }),
  })
}

export const usePageContainerDetailsQuery = (
  id?: string,
  options?: { enabled?: boolean }
): UseQueryResult<IPageContainer> => {
  return useQuery({
    enabled: !!id && options?.enabled !== false,
    queryFn: () => getPageContainer(id as string),
    queryKey: pageContainersKeys.details(id),
  })
}

////////MUTATION //////////

export const useCreatePageContainerMutation = (): UseMutationResult<
  IPageContainer,
  unknown,
  IPageContainer,
  unknown
> => {
  return useMutation({ mutationFn: createPageContainer })
}

export const useCreatePageContainerFromTemplateMutation = (): UseMutationResult<
  IPageContainer,
  unknown,
  IPageContainerCreateUpdate,
  unknown
> => {
  return useMutation({ mutationFn: createPageContainerFromTemplate })
}

export const useUpdatePageContainerFromTemplateMutation = (): UseMutationResult<
  IPageContainer,
  unknown,
  { data: IPageContainerCreateUpdate; id: string },
  unknown
> => {
  return useMutation({ mutationFn: updatePageContainerFromTemplateProcessor })
}

export const useProcessPageContainerMutation = (): UseMutationResult<IPageContainer, unknown, string, unknown> => {
  return useMutation({ mutationFn: processPageContainer })
}

export const useCompilePageContainerMutation = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: compilePageContainer })
}

export const useCheckPageContainerTranslationsMutation = (): UseMutationResult<
  { data: ITranslationRuleError[] | null },
  unknown,
  string,
  unknown
> => {
  return useMutation({ mutationFn: checkPageContainerTranslations })
}
